<script lang="ts" setup>
import { i18n } from '@/i18n';
import { useSearchForecastForReceiptStore } from './useSearchForecastForReceiptStore';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IYearMonth } from '@/coreDomainsModule/date';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import ControlCustomerPageTab from '../components/ControlCustomerPageTab.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { CUSTOMER_TAB_ROUTER_INFO, staffFilterTableHeaders, searchResultTableHeaders } from '../constants';
import EditForecastModal from '../components/EditForecastModal.vue';
import BulkEditConfirmModal from '../components/BulkEditConfirmModal.vue';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { openLoadingModal } from '@/corePagesModule/components/modal/modalActions';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';

onMounted(async () => {
  await initStaffFilter();
  await initSearchResult();
});

// 変数
const tabInfos = [...CUSTOMER_TAB_ROUTER_INFO];
const t = i18n.t;
const locale = computed(() => i18n.locale);
const router = useRouter();
const store = useSearchForecastForReceiptStore();
const { state } = storeToRefs(store);

// モーダル状態（ダウンロード）
const isDownloadModalOpen = ref(false);
const downloadTargetForecastForReceiptName = ref<string | undefined>(undefined);
const downloadTargetForecastForReceiptUserId = ref<string | undefined>(undefined);
const startYearMonth = ref<IYearMonth | undefined>(undefined);

// モーダル状態（一括編集）
const isEditModalOpen = ref(false);
const isConfirmModalOpen = ref(false);
const pendingEditData = ref<{
  selectedStaffIdList: string[];
}>({ selectedStaffIdList: [] });

// モーダル状態（行編集）
const isRowEditModalOpen = ref(false);
const isRowConfirmModalOpen = ref(false);
const editingRowId = ref<string | undefined>(undefined);
const pendingRowEditData = ref<{
  selectedStaffIdList: string[];
  forecastForReceiptName?: string;
}>({ selectedStaffIdList: [], forecastForReceiptName: undefined });

/**
 * 初期化処理
 */
const initStaffFilter = async () => {
  await store.fetchStaffFilterTableContents();
  state.value.staffFilterTableContents.renderingKey += 1;
};

/**
 * 検索結果テーブルの初期化処理
 */
const initSearchResult = async () => {
  await store.fetchSearchResultTableContents();
  state.value.searchResultTableContents.renderingKey += 1;
};

const isDisabledBulkEdit = computed(() => {
  return state.value.searchResultTableContents.checkedIds.length === 0;
});

/**
 * 月選択の最小年月（10年前）
 */
const monthPickerMinDate = computed((): IYearMonth => {
  const now = new Date();
  return {
    year: now.getFullYear() - 10,
    month: now.getMonth() + 1, // IYearMonth uses 1-indexed month
  };
});

/**
 * 月選択の最大年月（当月）
 */
const monthPickerMaxDate = computed((): IYearMonth => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1, // IYearMonth uses 1-indexed month
  };
});

/**
 * 一括編集ボタンをクリックした時の処理
 */
const onClickBulkEdit = () => {
  isEditModalOpen.value = true;
};

/**
 * 編集モーダルで適用をクリックした時の処理
 * @param selectedStaffIdList - 選択された担当者IDリスト
 */
const onApplyEdit = (selectedStaffIdList: string[]) => {
  pendingEditData.value = { selectedStaffIdList };
  isEditModalOpen.value = false;
  isConfirmModalOpen.value = true;
};

/**
 * 確認モーダルで保存をクリックした時の処理
 */
const onConfirmSave = async () => {
  const checkedDisplayIds = state.value.searchResultTableContents.checkedIds;
  // displayIdから実際のforecastForReceiptIdを取得
  const forecastForReceiptIds = state.value.searchResultTableContents.contentItems
    .filter((item) => checkedDisplayIds.includes(item.displayId))
    .map((item) => item.forecastForReceiptId as string)
    .filter((id): id is string => id !== undefined);
  const result = await store.bulkUpdateForecastForReceipt(forecastForReceiptIds, pendingEditData.value.selectedStaffIdList);
  isConfirmModalOpen.value = false;
  pendingEditData.value = { selectedStaffIdList: [] };
  if (result.status.type === 'success') {
    await initStaffFilter();
    await initSearchResult();
  }
};

/**
 * 確認モーダルでキャンセルをクリックした時の処理（編集モーダルに戻る）
 */
const onConfirmCancel = () => {
  isConfirmModalOpen.value = false;
  isEditModalOpen.value = true;
};

/**
 * 確認モーダルで保存せずに終了をクリックした時の処理
 */
const onConfirmDiscardAndClose = () => {
  isConfirmModalOpen.value = false;
  pendingEditData.value = { selectedStaffIdList: [] };
};

/**
 * 行の3点メニューから編集をクリックした時の処理
 * @param displayId - 編集対象の行ID
 */
const onClickRowEdit = (displayId: string) => {
  editingRowId.value = displayId;
  isRowEditModalOpen.value = true;
};

/**
 * 行編集モーダルで適用をクリックした時の処理
 * @param selectedStaffIdList - 選択された担当者IDリスト
 * @param forecastForReceiptName - 受領用内示名
 */
const onApplyRowEdit = (selectedStaffIdList: string[], forecastForReceiptName?: string) => {
  pendingRowEditData.value = { selectedStaffIdList, forecastForReceiptName };
  isRowEditModalOpen.value = false;
  isRowConfirmModalOpen.value = true;
};

/**
 * 行編集の確認モーダルで保存をクリックした時の処理
 */
const onRowConfirmSave = async () => {
  if (editingRowId.value) {
    // displayIdから実際のforecastForReceiptIdを取得
    const targetItem = state.value.searchResultTableContents.contentItems.find((item) => item.displayId === editingRowId.value);
    const forecastForReceiptId = targetItem?.forecastForReceiptId as string | undefined;
    if (forecastForReceiptId) {
      const result = await store.bulkUpdateForecastForReceipt(
        [forecastForReceiptId],
        pendingRowEditData.value.selectedStaffIdList,
        pendingRowEditData.value.forecastForReceiptName
      );
      isRowConfirmModalOpen.value = false;
      editingRowId.value = undefined;
      pendingRowEditData.value = { selectedStaffIdList: [], forecastForReceiptName: undefined };
      if (result.status.type === 'success') {
        await initStaffFilter();
        await initSearchResult();
      }
    }
  }
};

/**
 * 行編集の確認モーダルでキャンセルをクリックした時の処理（編集モーダルに戻る）
 */
const onRowConfirmCancel = () => {
  isRowConfirmModalOpen.value = false;
  isRowEditModalOpen.value = true;
};

/**
 * 行編集の確認モーダルで保存せずに終了をクリックした時の処理
 */
const onRowConfirmDiscardAndClose = () => {
  isRowConfirmModalOpen.value = false;
  editingRowId.value = undefined;
  pendingRowEditData.value = { selectedStaffIdList: [], forecastForReceiptName: undefined };
};

/**
 * 担当者テーブルのダウンロードをクリックした時の処理
 * @param displayId - 対象の行ID
 */
const onClickStaffDownload = (displayId: string) => {
  const targetItem = state.value.staffFilterTableContents.contentItems.find((item) => item.displayId === displayId);
  const forecastForReceiptResponsibleUserId = targetItem?.forecastStaffId as string | undefined;
  if (forecastForReceiptResponsibleUserId) {
    downloadTargetForecastForReceiptUserId.value = forecastForReceiptResponsibleUserId;
    isDownloadModalOpen.value = true;
  }
};

/**
 * 検索結果テーブルのダウンロードをクリックした時の処理
 * @param displayId - 対象の行ID
 */
const onClickSearchResultDownload = (displayId: string) => {
  const targetItem = state.value.searchResultTableContents.contentItems.find((item) => item.displayId === displayId);
  const forecastForReceiptResponsibleUserId = targetItem?.forecastForReceiptResponsibleUsers?.[0]?.email as string | undefined;
  const forecastForReceiptName = targetItem?.forecastForReceiptName as string | undefined;
  if (forecastForReceiptResponsibleUserId && forecastForReceiptName) {
    downloadTargetForecastForReceiptUserId.value = forecastForReceiptResponsibleUserId;
    downloadTargetForecastForReceiptName.value = forecastForReceiptName;
    isDownloadModalOpen.value = true;
  }
};

/**
 * ダウンロードモーダルを閉じる
 */
const onCloseDownloadModal = () => {
  isDownloadModalOpen.value = false;
  downloadTargetForecastForReceiptUserId.value = undefined;
  downloadTargetForecastForReceiptName.value = undefined;
  startYearMonth.value = undefined;
};

/**
 * ダウンロードモーダルで適用をクリックした時の処理
 */
const onApplyDownload = async () => {
  const confirmedStartYearMonth = startYearMonth.value;
  if (!downloadTargetForecastForReceiptUserId.value || !confirmedStartYearMonth) return;

  isDownloadModalOpen.value = false;
  const forecastForReceiptUserId = downloadTargetForecastForReceiptUserId.value;
  const forecastForReceiptName = downloadTargetForecastForReceiptName.value;

  /**
   * ダウンロード処理を実行する内部関数
   */
  const executeDownload = async () => {
    return await store.downloadForecastForReceipt(forecastForReceiptUserId, confirmedStartYearMonth, forecastForReceiptName);
  };

  const result = await openLoadingModal(t('出力中'), executeDownload, []);

  if (result) {
    toast.success(t('ダウンロードしました'));
  } else {
    toast.error(t('ダウンロードに失敗しました'));
  }

  downloadTargetForecastForReceiptUserId.value = undefined;
  downloadTargetForecastForReceiptName.value = undefined;
};

/**
 * 担当者テーブルの内示依頼メールをクリックした時の処理
 * @param displayId - 対象の行ID
 */
const onClickStaffForecastRequestMail = (displayId: string) => {
  // TODO: 内示依頼メール処理を実装
  console.log('staff forecast request mail', displayId);
};

/**
 * 検索結果テーブルの取引一覧ボタンをクリックした時の処理
 * 全取引タブに遷移し、該当の受領用内示が選択された状態で表示する
 * @param displayId - 対象の行ID
 */
const onClickTransactionList = (displayId?: string) => {
  if (!displayId) return;

  // displayIdから実際のforecastForReceiptIdを取得
  const targetItem = state.value.searchResultTableContents.contentItems.find((item) => item.displayId === displayId);
  const forecastForReceiptId = targetItem?.forecastForReceiptId as string | undefined;

  if (forecastForReceiptId) {
    // 全取引タブに遷移（受領用内示IDをパスパラメータとして渡す）
    router.push({
      name: 'customer-transaction-list',
      params: { forecastForReceiptId },
    });
  }
};
</script>

<template>
  <div class="receive-forecast-search">
    <ControlCustomerPageTab class="receive-forecast-search__tab-bar" :tab-info-object="tabInfos" />
    <div class="receive-forecast-search__main">
      <div class="receive-forecast-search__instruction">{{ t('forecastForReceipt_searchInstruction') }}</div>
      <div class="receive-forecast-search__filter-content filter-content">
        <div class="filter-content__item">
          <div class="filter-content__title">{{ t('forecastForReceipt_searchFilterByStaff') }}</div>
          <NewBaseTableContent
            :key="state.staffFilterTableContents.renderingKey"
            class="filter-content__table"
            :prop-table-content-properties="staffFilterTableHeaders"
            :prop-display-objects="state.staffFilterTableContents.contentItems"
            :prop-loading-status="state.staffFilterTableContents.loadingState"
            :prop-table-type="'forecastForReceiptStaffFilterTable'"
            @checked="store.onCheckedStaffFilterDisplayIds"
            @on-click-download="onClickStaffDownload"
            @on-click-forecast-request-mail="onClickStaffForecastRequestMail"
          />
        </div>
      </div>
      <div class="receive-forecast-search__result-content result-content">
        <div class="result-content__title">{{ t('forecastForReceipt_searchResultTitle') }}</div>
        <div class="result-content__select-month-area select-month-area">
          <div class="select-month-area__title">{{ t('forecastForReceipt_searchReceiveDate') }}</div>
          <MonthPicker
            class="select-month-area__calendar"
            :model-value="state.receivedForecastYearMonth"
            :locale="locale"
            :prop-format="t('MonthPickerFormat')"
            :min-date="monthPickerMinDate"
            :max-date="monthPickerMaxDate"
            @update:model-value="store.onSelectedReceiveMonth($event)"
          />
          <TextButton
            class="select-month-area__bulk-edit-button bulk-edit-button"
            :class="{ 'bulk-edit-button--enabled': !isDisabledBulkEdit }"
            :disabled="isDisabledBulkEdit"
            @click="onClickBulkEdit"
          >
            <div class="bulk-edit-button__container">
              <span class="bulk-edit-button__text">{{ t('一括編集') }}</span>
              <SvgLoader class="bulk-edit-button__icon" name="editPen" />
            </div>
          </TextButton>
        </div>
        <NewBaseTableContent
          :key="state.searchResultTableContents.renderingKey"
          class="result-content__table"
          :prop-table-content-properties="searchResultTableHeaders"
          :prop-display-objects="state.searchResultTableContents.contentItems"
          :prop-loading-status="state.searchResultTableContents.loadingState"
          :prop-table-type="'forecastForReceiptSearchResultTable'"
          @checked="store.onCheckedSearchResultDisplayIds"
          @on-click-edit="onClickRowEdit"
          @on-click-transaction-list="onClickTransactionList"
          @on-click-download="onClickSearchResultDownload"
        />
      </div>
    </div>
  </div>

  <!-- 一括編集モーダル -->
  <EditForecastModal :modal-state="isEditModalOpen" :select-input-index="30" @close-modal="isEditModalOpen = false" @apply="onApplyEdit" />

  <!-- 一括編集の保存確認モーダル -->
  <BulkEditConfirmModal
    :modal-state="isConfirmModalOpen"
    @save="onConfirmSave"
    @cancel="onConfirmCancel"
    @discard-and-close="onConfirmDiscardAndClose"
  />

  <!-- 行編集モーダル -->
  <EditForecastModal
    :modal-state="isRowEditModalOpen"
    :select-input-index="31"
    :show-forecast-name="true"
    @close-modal="isRowEditModalOpen = false"
    @apply="onApplyRowEdit"
  />

  <!-- 行編集の保存確認モーダル -->
  <BulkEditConfirmModal
    :modal-state="isRowConfirmModalOpen"
    @save="onRowConfirmSave"
    @cancel="onRowConfirmCancel"
    @discard-and-close="onRowConfirmDiscardAndClose"
  />

  <!-- ダウンロードモーダル -->
  <InfoModal
    :modal-state="isDownloadModalOpen"
    :back-button-label="t('もどる')"
    :next-button-label="t('ダウンロード')"
    :is-disabled-next-button="!startYearMonth"
    @on-back="onCloseDownloadModal"
    @on-next="onApplyDownload"
  >
    <template #title>{{ t('受領用内示ダウンロード') }}</template>
    <div class="fof-download">
      <div class="fof-download__picker-container">
        <div class="fof-download__picker-item">
          <label class="fof-download__picker-label">{{ t('開始月') }}</label>
          <MonthPicker
            class="fof-download__calendar select-month-area__calendar"
            :model-value="startYearMonth"
            :locale="locale"
            :prop-format="'yyyy / MM'"
            :max-date="DateService.getShiftedIYearMonthFromNowDateByMonths(0)"
            @update:model-value="startYearMonth = $event"
          />
        </div>
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.receive-forecast-search {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 16px;

  &__main {
    background-color: $semanticWhite;
    height: 100%;
    padding: 24px 16px;
    border-radius: 4px;
    display: grid;
    grid-template-rows: 20px 178px 1fr;
    gap: 16px;
    font: Noto Sans JP;
    letter-spacing: 0;
    white-space: nowrap;
  }

  &__instruction {
    font-size: 14px;
    line-height: 20.27px;
    font-weight: 400;
    color: $textDefault;
  }
}

.filter-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  &__item {
    display: grid;
    grid-template-rows: 14px 156px;
    gap: 8px;
  }

  &__title {
    font-size: 14px;
    line-height: 14px;
    font-weight: 700;
    color: $textDefault;
  }

  &__table {
    overflow-y: scroll !important;
    min-height: 100% !important;
    max-height: 100% !important;
    background-color: $backgroundDefault;
    border: 1px solid $borderDefault;
  }
}

.result-content {
  display: grid;
  grid-template-rows: 14px auto 1fr;
  gap: 8px;

  &__title {
    font-size: 14px;
    line-height: 14px;
    font-weight: 700;
    color: $textDefault;
  }

  &__table {
    background-color: $backgroundDefault;
    min-height: 300px;
    border: 1px solid $borderDefault;
  }
}

.select-month-area {
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-content: start;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;

  &__title {
    font-size: 14px;
    line-height: 20.27px;
    font-weight: 400;
    color: $textDefault;
  }

  &__calendar {
    width: 141px;
    height: 36px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }

  .bulk-edit-button {
    height: 36px;
    padding: 0 16px;
    background-color: $textSecondary;
    border-radius: 4px;
    display: grid;
    place-items: center;

    &--enabled {
      background-color: $semanticPrimary;
    }

    &__container {
      display: grid;
      grid-template-columns: auto 20px;
      place-items: center;
      gap: 4px;
    }

    &__text {
      font-size: 14px;
      font-weight: 700;
      color: $semanticWhite;
    }

    &__icon {
      color: $semanticWhite;
      width: 20px;
      height: 20px;
    }
  }
}

.fof-download {
  background-color: $backgroundDefault;
  border-radius: 8px;
  display: grid;
  grid-template-rows: auto auto;
  gap: 16px;
  padding: 16px;

  &__label {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__picker-container {
    display: grid;
    grid-template-columns: auto auto;
    gap: 24px;
    align-items: center;
  }

  &__picker-item {
    display: grid;
    grid-template-columns: auto auto;
    gap: 8px;
    align-items: center;
  }

  &__picker-label {
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
  }

  &__calendar {
    min-width: 180px;
    max-width: 180px;
    height: 36px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }
}
</style>

<style lang="scss">
.select-month-area__calendar input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
}
</style>
